import styled from '@emotion/styled';
import {
  palette,
  radius,
  shadow,
  spacing,
  transition,
  typography,
  easing,
  duration,
  gradient,
  layout,
} from '../../../style-utils/theme';
import { up, down } from '../../../style-utils/media-queries';
import { StateSwitch } from '../../../motion';

/* The report's three fills (skeleton, error, table) crossfade inside this;
   ContentContainer centres its children, so the switch and its motion layer
   have to claim the full width for the table to keep its own max-width. */
export const ReportSwitch = styled(StateSwitch)`
  width: 100%;

  > div {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
  }

  /* A centred flex column sizes each fill to its CONTENT, so the Staleable
     around the table grew to the table's natural 765px on a phone and the
     whole page scrolled sideways (device sweep, 320/390, once the day had
     entries). Capped at the column, the table's own overflow-x takes over. */
  > div > * {
    max-width: 100%;
    min-width: 0;
  }
`;

export const ButtonsWrapper = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: flex-end;
  gap: ${spacing.xs};
  width: 100%;
  padding: ${spacing.md} ${spacing.md} 0;

  ${down('md')} {
    padding: ${spacing.sm} ${spacing.sm} 0;
  }
`;

export const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 9;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  margin: 0 auto;
  padding: ${spacing.md};

  ${down('md')} {
    padding: ${spacing.sm};
  }
`;

/* ── Premium report table ──────────────────────────────────────────────
 * Unified table language: surface card, hairline line dividers, slate caps
 * header, readable Inter ramp. Scrolls horizontally under md (never clips)
 * so it stays print/PDF-friendly (html2canvas/jspdf capture the full node). */

export const ReportTableWrapper = styled.div`
  width: 100%;
  max-width: ${layout.contentMaxWidth};
  min-width: 0;
  box-sizing: border-box;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  box-shadow: ${shadow.sm};

  /* Soft, branded scrollbar */
  scrollbar-width: thin;
  scrollbar-color: ${palette.lineStrong} transparent;
  &::-webkit-scrollbar {
    height: ${spacing.xs};
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${palette.lineStrong};
    border-radius: ${radius.pill};
  }
`;

export const ReportTable = styled.table`
  width: 100%;
  min-width: 560px;
  border-collapse: collapse;
  margin: 0;
  background: ${palette.surface};
  border-radius: ${radius.md};
  overflow: hidden;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.ink};
  letter-spacing: ${typography.tracking.snug};

  thead th {
    text-align: left;
    white-space: nowrap;
    padding: ${spacing.sm} ${spacing.md};
    background: ${palette.surfaceMuted};
    color: ${palette.slate};
    font-weight: ${typography.weight.semibold};
    font-size: ${typography.size.xxs}px;
    letter-spacing: ${typography.tracking.caps};
    text-transform: uppercase;
    border-bottom: 1px solid ${palette.lineStrong};
  }

  tbody tr {
    transition: ${transition.colors};
  }

  tbody tr:nth-of-type(even) {
    background: ${palette.surfaceMuted};
  }

  tbody tr:hover {
    background: ${palette.irisTintSoft};
  }

  tbody td {
    padding: ${spacing.sm} ${spacing.md};
    max-width: 320px;
    color: ${palette.inkSoft};
    line-height: ${typography.leading.normal};
    vertical-align: top;
    white-space: normal;
    word-wrap: break-word;
    overflow-wrap: anywhere;
    border-bottom: 1px solid ${palette.line};
  }

  tbody td > div + div {
    margin-top: ${spacing.half};
  }

  tbody tr:last-of-type td {
    border-bottom: none;
  }

  ${down('md')} {
    font-size: ${typography.size.xs}px;

    thead th {
      padding: ${spacing.xs} ${spacing.sm};
    }
    tbody td {
      padding: ${spacing.xs} ${spacing.sm};
    }
  }

  ${up('lg')} {
    font-size: ${typography.size.sm}px;
  }
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${spacing.xs};
  width: 100%;
  max-width: ${layout.contentMaxWidth};
  min-height: 200px;
  box-sizing: border-box;
  padding: ${spacing.xxl} ${spacing.md};
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  box-shadow: ${shadow.sm};
  color: ${palette.slate};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  letter-spacing: ${typography.tracking.snug};
  text-align: center;
`;

export const LoaderWrapper = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 240px;
  padding: ${spacing.xxl} ${spacing.md};
`;

/**
 * THE REPORT TOGGLE.
 *
 * Moved here from `dashboard-home.styled.tsx` when the Dashboard consolidation
 * deleted that screen. This is the only surface that still uses it, so it lives
 * with its one consumer rather than in a shared file nobody owns — and it is
 * renamed, because a component called DashboardHomeButton on a screen that is
 * not the dashboard home is a name that lies.
 *
 * The `isSegment` variant came with it and is unused here; it is kept because
 * the visual contract of the default variant is defined by contrast with it, and
 * because deleting a branch while moving a file is two changes wearing one
 * diff.
 */
export const ReportToggleButton = styled.button<{
  isSelected: boolean;
  isExportToPdfButton?: boolean;
  isSegment?: boolean;
}>`
  cursor: pointer;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  letter-spacing: ${typography.tracking.snug};
  border: none;
  transition: ${transition.colors}, transform ${duration.base} ${easing.spring};

  ${({ isSegment, isSelected }) =>
    isSegment
      ? `
    position: relative;
    z-index: 1;
    /* The parent is a grid now and sizes the columns; the flex basis and the
       72px floor that came with it are what squeezed the label in the first
       place. nowrap is the belt: a period label is one short word and must
       never wrap, whatever a future container does to it. */
    white-space: nowrap;
    height: ${layout.controlHeight};
    padding: 0 ${spacing.sm};
    background: transparent;
    border-radius: ${radius.pill};
    color: ${isSelected ? palette.white : palette.slate};
    font-weight: ${typography.weight.semibold};

    &:hover { color: ${isSelected ? palette.white : palette.ink}; }
    &:active { transform: scale(0.97); }
    &:focus-visible { outline: none; box-shadow: ${shadow.focus}; }
  `
      : `
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: ${spacing.xs};
    width: fit-content;
    height: ${layout.controlHeight};
    min-height: ${layout.controlHeight};
    padding: 0 ${spacing.md};
    background: ${gradient.primary};
    color: ${palette.white};
    border-radius: ${radius.sm};
    box-shadow: ${shadow.brand};
    font-weight: ${typography.weight.semibold};

    &:hover {
      background: ${gradient.primaryHover};
      box-shadow: ${shadow.brandHover};
      transform: translateY(-1px);
    }
    &:active { transform: scale(0.97); }
    &:focus-visible { outline: none; box-shadow: ${shadow.focus}; }
  `}

  ${({ isSegment, isExportToPdfButton }) =>
    !isSegment && isExportToPdfButton ? `margin-top: ${spacing.sm};` : ''}

  &:disabled {
    cursor: not-allowed;
    background: ${palette.surfaceMuted};
    color: ${palette.slateLight};
    box-shadow: none;
    transform: none;
  }
`;

/**
 * WHICH DESK DAY THIS REPORT COVERS.
 *
 * Quiet by design — it is a caption, not a heading. It exists because the desk
 * day runs 07:00 to 07:00 and this screen has no date control, so without it the
 * only way to know which day you are reading is to already know the rule.
 */
export const DeskDayNote = styled.p`
  width: 100%;
  max-width: ${layout.contentMaxWidth};
  margin: 0 0 ${spacing.sm};
  color: ${palette.slate};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  line-height: ${typography.leading.normal};
  text-align: left;
`;
